import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import {
  Package, Search, Plus, Edit2, Trash2, Send, ChevronDown, Filter, DollarSign, Truck, Tag, X, CheckCircle, AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/catalog-mgmt`;

// ── Fuzzy name matching for duplicate detection ──
const _normName = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const _bigrams = (s) => {
  const t = _normName(s).replace(/ /g, '');
  const b = new Set();
  for (let i = 0; i < t.length - 1; i++) b.add(t.slice(i, i + 2));
  return b;
};
const _diceScore = (a, b) => {
  const A = _bigrams(a), B = _bigrams(b);
  if (!A.size || !B.size) return 0;
  let inter = 0;
  A.forEach(x => { if (B.has(x)) inter++; });
  return (2 * inter) / (A.size + B.size);
};
const findSimilarItems = (allItems, name, excludeId) => {
  const n = _normName(name);
  if (!n) return [];
  return allItems
    .filter(i => i.is_active !== false && i.id !== excludeId)
    .map(i => {
      const ni = _normName(i.name);
      let score = _diceScore(i.name, name);
      if (ni === n) score = 1;
      else if (ni.includes(n) || n.includes(ni)) score = Math.max(score, 0.9);
      return { item: i, score };
    })
    .filter(m => m.score >= 0.6)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);
};

// ── CATALOG TAB (module-level so parent re-renders don't remount it: keeps
//    open categories, scroll position and search focus intact) ──
const CatalogTabView = ({ items, suppliers, categories, search, setSearch, filterCat, setFilterCat, openCats, setOpenCats, onAddItem, onEditItem, onDeleteItem }) => {
  const grouped = {};
  items.forEach(i => {
    const cat = i.category || 'Other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(i);
  });
  const cats = Object.keys(grouped).sort();
  const toggleCat = (c) => setOpenCats(prev => ({ ...prev, [c]: !prev[c] }));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search items..."
            data-testid="catalog-search-input"
            className="w-full pl-10 pr-4 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm placeholder-slate-400 outline-none" />
        </div>
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
          className="px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none">
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={onAddItem} data-testid="catalog-add-item-btn"
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> Add Item
        </button>
      </div>

      <p className="text-xs text-slate-500">{items.length} items{filterCat ? ` in ${filterCat}` : ''}</p>

      {cats.map(cat => (
        <div key={cat} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <button onClick={() => toggleCat(cat)}
            className="w-full px-4 py-2.5 bg-slate-100 border-b border-slate-200 flex items-center justify-between hover:bg-slate-100 transition-colors">
            <h3 className="text-sm font-semibold text-slate-900">{cat} ({grouped[cat].length})</h3>
            <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${openCats[cat] ? 'rotate-180' : ''}`} />
          </button>
          {openCats[cat] && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 border-b border-slate-200">
                  <th className="px-4 py-2 text-left sticky left-0 bg-slate-100 z-10">Item</th>
                  <th className="px-3 py-2 text-left">Part #</th>
                  <th className="px-3 py-2 text-left">Size</th>
                  <th className="px-3 py-2 text-center">Unit</th>
                  <th className="px-3 py-2 text-right">Cost</th>
                  <th className="px-3 py-2 text-center">Markup</th>
                  <th className="px-3 py-2 text-right">Sell</th>
                  <th className="px-3 py-2 text-left">Supplier</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {grouped[cat].map(item => {
                  const sup = suppliers.find(s => s.id === item.supplier_id);
                  return (
                    <tr key={item.id} className="border-b border-slate-200 hover:bg-slate-100">
                      <td className="px-4 py-2 text-slate-900 font-medium sticky left-0 bg-white z-10">{item.name}</td>
                      <td className="px-3 py-2 text-slate-500 font-mono text-xs">{item.part_number || '—'}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{item.size_variant || '—'}</td>
                      <td className="px-3 py-2 text-center text-slate-500">{item.pricing_unit || item.unit}</td>
                      <td className="px-3 py-2 text-right text-slate-900 font-mono">${(item.unit_cost || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-center text-amber-700">{item.markup_pct ? `${item.markup_pct}%` : '—'}</td>
                      <td className="px-3 py-2 text-right text-emerald-700 font-mono font-bold">{item.sell_price ? `$${item.sell_price.toFixed(2)}` : '—'}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{sup?.name || '—'}</td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          <button onClick={() => onEditItem(item)}
                            className="p-1 text-slate-500 hover:text-slate-900"><Edit2 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => onDeleteItem(item)}
                            className="p-1 text-slate-500 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default function CatalogManagement() {
  const { user } = useAuth();
  const [tab, setTab] = useState('catalog');
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [openCats, setOpenCats] = useState({});

  // Edit/Add
  const [editItem, setEditItem] = useState(null);
  const [editSupplier, setEditSupplier] = useState(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showAddSupplier, setShowAddSupplier] = useState(false);

  // Bulk markup
  const [bulkCat, setBulkCat] = useState('');
  const [bulkMarkup, setBulkMarkup] = useState('');

  // Order
  const [orderItems, setOrderItems] = useState({});
  const [orderSupplier, setOrderSupplier] = useState('');
  const [orderMsg, setOrderMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [orderHistory, setOrderHistory] = useState([]);

  const load = useCallback(async () => {
    try {
      const [itemsR, suppR, catsR] = await Promise.all([
        axios.get(`${API}/items`, { params: { category: filterCat || undefined, search: search || undefined } }),
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/categories`),
      ]);
      setItems(itemsR.data);
      setSuppliers(suppR.data);
      setCategories(catsR.data);
    } catch (e) {
      if (e.response?.status !== 403) toast.error('Failed to load');
    }
    setLoading(false);
  }, [filterCat, search]);

  useEffect(() => { load(); }, [load]);

  const tabs = [
    { id: 'catalog', label: 'Catalog', icon: Package },
    { id: 'suppliers', label: 'Suppliers', icon: Truck },
    { id: 'order', label: 'Order Items', icon: Send },
    { id: 'markup', label: 'Markup', icon: DollarSign },
  ];

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete ${item.name}?`)) return;
    await axios.delete(`${API}/items/${item.id}`);
    toast.success('Deactivated'); load();
  };

  // ── SUPPLIERS TAB ──
  const SuppliersTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{suppliers.length} suppliers</p>
        <button onClick={() => { setShowAddSupplier(true); setEditSupplier(null); }}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> Add Supplier
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {suppliers.map(s => (
          <div key={s.id} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-start justify-between mb-2">
              <h4 className="text-slate-900 font-semibold">{s.name}</h4>
              <div className="flex gap-1">
                <button onClick={() => { setEditSupplier(s); setShowAddSupplier(true); }}
                  className="p-1 text-slate-500 hover:text-slate-900"><Edit2 className="w-3.5 h-3.5" /></button>
                <button onClick={async () => {
                  if (!window.confirm(`Delete ${s.name}?`)) return;
                  await axios.delete(`${API}/suppliers/${s.id}`); toast.success('Deleted'); load();
                }} className="p-1 text-slate-500 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            {s.contact_name && <p className="text-sm text-slate-700">{s.contact_name}</p>}
            {s.email && <p className="text-xs text-blue-700">{s.email}</p>}
            {s.phone && <p className="text-xs text-slate-500">{s.phone}</p>}
            {s.account_number && <p className="text-xs text-slate-500 mt-1">Acct: {s.account_number}</p>}
          </div>
        ))}
      </div>
    </div>
  );

  // ── ORDER TAB ──
  const OrderTab = () => {
    const selectedItems = Object.entries(orderItems).filter(([_, v]) => v.qty > 0);
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Select Items to Order</h3>
          <div className="max-h-[400px] overflow-y-auto space-y-1">
            {items.filter(i => i.is_active !== false).map(item => {
              const sel = orderItems[item.id];
              return (
                <div key={item.id} className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-colors ${sel?.qty > 0 ? 'border-blue-300 bg-blue-50' : 'border-slate-200 hover:bg-slate-100'}`}>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-slate-900">{item.name}</span>
                    {item.part_number && <span className="text-xs text-slate-500 ml-2">#{item.part_number}</span>}
                    <span className="text-xs text-slate-500 ml-2">{item.category}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <input type="number" min="0" value={sel?.qty || ''} placeholder="0"
                      onChange={e => setOrderItems(prev => ({ ...prev, [item.id]: { ...prev[item.id], qty: parseInt(e.target.value) || 0, notes: prev[item.id]?.notes || '' } }))}
                      className="w-16 px-2 py-1 bg-white/90 border border-slate-300 rounded text-slate-900 text-sm text-center outline-none" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {selectedItems.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-900">Send Order ({selectedItems.length} items)</h3>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Send To Supplier</label>
              <select value={orderSupplier} onChange={e => setOrderSupplier(e.target.value)}
                className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none">
                <option value="">Select supplier...</option>
                {suppliers.filter(s => s.email).map(s => <option key={s.id} value={s.id}>{s.name} ({s.email})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Message (optional)</label>
              <textarea value={orderMsg} onChange={e => setOrderMsg(e.target.value)} rows={2}
                className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" />
            </div>
            <button disabled={!orderSupplier || sending} onClick={async () => {
              setSending(true);
              try {
                const payload = {
                  supplier_id: orderSupplier,
                  items: selectedItems.map(([id, v]) => ({ item_id: id, quantity: v.qty, notes: v.notes || '' })),
                  message: orderMsg,
                };
                await axios.post(`${API}/send-order`, payload);
                toast.success('Order sent to supplier!');
                setOrderItems({}); setOrderMsg('');
              } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
              setSending(false);
            }} className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 text-white rounded-lg font-semibold flex items-center justify-center gap-2">
              <Send className="w-4 h-4" /> {sending ? 'Sending...' : 'Send Order Email'}
            </button>
          </div>
        )}
      </div>
    );
  };

  // ── MARKUP TAB ──
  const MarkupTab = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Bulk Set Markup</h3>
        <p className="text-xs text-slate-500 mb-4">Apply a markup percentage to all items in a category (or all items). Sell price will be auto-calculated from cost + markup.</p>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Category</label>
            <select value={bulkCat} onChange={e => setBulkCat(e.target.value)}
              className="px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none">
              <option value="">All Items</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Markup %</label>
            <input type="number" value={bulkMarkup} onChange={e => setBulkMarkup(e.target.value)} min="0" max="500" placeholder="15"
              className="w-24 px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" />
          </div>
          <button onClick={async () => {
            const pct = parseFloat(bulkMarkup);
            if (isNaN(pct) || pct < 0) { toast.error('Enter a valid markup %'); return; }
            if (!window.confirm(`Set ${pct}% markup on ${bulkCat || 'ALL'} items?`)) return;
            const r = await axios.post(`${API}/bulk-markup`, { markup_pct: pct, category: bulkCat || undefined });
            toast.success(`Updated ${r.data.updated} items to ${pct}% markup`);
            load();
          }} className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium">
            Apply Markup
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Markup Summary by Category</h3>
        <div className="space-y-2">
          {categories.map(cat => {
            const catItems = items.filter(i => i.category === cat);
            const avgMarkup = catItems.reduce((s, i) => s + (i.markup_pct || 0), 0) / (catItems.length || 1);
            const totalCost = catItems.reduce((s, i) => s + (i.unit_cost || 0), 0);
            const totalSell = catItems.reduce((s, i) => s + (i.sell_price || i.unit_cost || 0), 0);
            return (
              <div key={cat} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-100">
                <span className="text-sm text-slate-900">{cat} <span className="text-slate-500">({catItems.length})</span></span>
                <div className="flex gap-4 text-xs">
                  <span className="text-slate-500">Avg: {avgMarkup.toFixed(0)}%</span>
                  <span className="text-slate-500">Cost: ${totalCost.toFixed(0)}</span>
                  <span className="text-emerald-700 font-bold">Sell: ${totalSell.toFixed(0)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // ── ADD/EDIT ITEM MODAL ──
  const ItemModal = () => {
    const blankForm = {
      name: '', category: categories[0] || 'Other', unit: 'each', unit_cost: 0,
      markup_pct: 0, sell_price: 0, part_number: '', supplier_id: '', size_variant: '',
      pricing_unit: 'each', notes: '',
    };
    const [form, setForm] = useState(editItem || blankForm);
    const [dupMatches, setDupMatches] = useState(null);
    const [saving, setSaving] = useState(false);
    const isEdit = !!editItem;

    const closeModal = () => { setShowAddItem(false); setEditItem(null); setDupMatches(null); };

    const doSave = async (keepOpen = false) => {
      setSaving(true);
      try {
        if (isEdit) {
          await axios.put(`${API}/items/${editItem.id}`, form);
          toast.success('Updated');
        } else {
          await axios.post(`${API}/items`, form);
          toast.success(`Added ${form.name}`);
        }
        // Keep the user's place: open the item's category group so it shows inline
        const cat = form.category || 'Other';
        setOpenCats(prev => ({ ...prev, [cat]: true }));
        load();
        if (keepOpen) {
          // Bulk entry: keep category/supplier/markup, clear the item-specific fields
          setForm(f => ({ ...f, name: '', part_number: '', size_variant: '', unit_cost: 0, sell_price: 0, notes: '' }));
          setDupMatches(null);
        } else {
          closeModal();
        }
      } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
      setSaving(false);
    };

    const save = async (keepOpen = false) => {
      if (!(form.name || '').trim()) { toast.error('Name is required'); return; }
      if (!isEdit && dupMatches === null) {
        // Fuzzy duplicate check against the FULL catalog (ignores current filters)
        setSaving(true);
        try {
          const r = await axios.get(`${API}/items`);
          const matches = findSimilarItems(r.data, form.name);
          setSaving(false);
          if (matches.length > 0) { setDupMatches({ matches, keepOpen }); return; }
        } catch { setSaving(false); }
      }
      doSave(keepOpen);
    };

    const resolveModifyExisting = (existing) => {
      setDupMatches(null);
      setEditItem(existing); // reopens this modal in edit mode for the existing item
    };

    const resolveDeleteAndAdd = async (existing) => {
      if (!window.confirm(`Delete existing "${existing.name}" and add "${form.name}"?`)) return;
      try {
        await axios.delete(`${API}/items/${existing.id}`);
        toast.success(`Deleted ${existing.name}`);
        await doSave(dupMatches?.keepOpen || false);
      } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
    };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="bg-white rounded-xl border border-slate-200 w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-900">{isEdit ? 'Edit Item' : 'New Item'}</h3>
            <button onClick={closeModal}><X className="w-5 h-5 text-slate-500" /></button>
          </div>

          {/* ── Duplicate resolution box ── */}
          {dupMatches && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3" data-testid="catalog-dup-box">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0" />
                <p className="text-sm font-semibold text-amber-700">Similar item{dupMatches.matches.length > 1 ? 's' : ''} already in the catalog</p>
              </div>
              <div className="space-y-2">
                {dupMatches.matches.map(({ item: m, score }) => (
                  <div key={m.id} className="bg-slate-50 rounded-lg p-3" data-testid={`catalog-dup-match-${m.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <p className="text-sm text-slate-900 font-medium">{m.name}</p>
                        <p className="text-xs text-slate-500">
                          {m.category || 'Other'} · ${(m.unit_cost || 0).toFixed(2)}
                          {m.part_number ? ` · #${m.part_number}` : ''}
                          {score >= 0.99 ? ' · exact name match' : ''}
                        </p>
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        <button onClick={() => resolveModifyExisting(m)} data-testid={`catalog-dup-modify-${m.id}`}
                          className="px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium">
                          Modify Existing
                        </button>
                        <button onClick={() => resolveDeleteAndAdd(m)} data-testid={`catalog-dup-delete-${m.id}`}
                          className="px-2.5 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-medium">
                          Delete & Add Mine
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={closeModal} data-testid="catalog-dup-keep-original"
                  className="flex-1 py-2 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-lg text-xs font-semibold">
                  Keep Original — Don't Add
                </button>
                <button onClick={() => doSave(dupMatches.keepOpen)} data-testid="catalog-dup-add-anyway"
                  className="flex-1 py-2 bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg text-xs font-semibold">
                  Add Anyway (it's different)
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs text-slate-500">Name *</label>
              <input value={form.name || ''} onChange={e => { setForm({ ...form, name: e.target.value }); if (dupMatches) setDupMatches(null); }}
                data-testid="catalog-item-name-input"
                className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Category</label>
              <select value={form.category || ''} onChange={e => {
                if (e.target.value === '__NEW__') {
                  const newCat = window.prompt('New category name:');
                  if (newCat) setForm({ ...form, category: newCat.trim().toUpperCase() });
                } else {
                  setForm({ ...form, category: e.target.value });
                }
              }} className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none">
                <option value="">Select...</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
                {form.category && !categories.includes(form.category) && (
                  <option value={form.category}>{form.category} (new)</option>
                )}
                <option value="__NEW__">+ New Category...</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Part Number</label>
              <input value={form.part_number || ''} onChange={e => setForm({ ...form, part_number: e.target.value })}
                className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Size / Variant</label>
              <input value={form.size_variant || ''} onChange={e => setForm({ ...form, size_variant: e.target.value })}
                placeholder="e.g., T cyl, M cyl, 5kg box"
                className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Pricing Unit</label>
              <select value={form.pricing_unit || 'each'} onChange={e => setForm({ ...form, pricing_unit: e.target.value })}
                className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none">
                <option value="each">Each</option>
                <option value="per lb">Per Pound</option>
                <option value="per stick">Per Stick</option>
                <option value="per box">Per Box</option>
                <option value="per lot">Per Lot (assorted)</option>
                <option value="per ft">Per Foot</option>
                <option value="per cylinder">Per Cylinder</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Cost Price ($)</label>
              <input type="number" step="0.01" value={form.unit_cost || ''} onChange={e => {
                const cost = parseFloat(e.target.value) || 0;
                const markup = form.markup_pct || 0;
                const autoSell = markup > 0 && cost > 0 ? Math.round(cost * (1 + markup / 100) * 100) / 100 : form.sell_price;
                setForm({ ...form, unit_cost: cost, sell_price: autoSell });
              }} className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Markup %</label>
              <input type="number" step="0.5" value={form.markup_pct || ''} onChange={e => {
                const markup = parseFloat(e.target.value) || 0;
                const cost = form.unit_cost || 0;
                const autoSell = markup > 0 && cost > 0 ? Math.round(cost * (1 + markup / 100) * 100) / 100 : form.sell_price;
                setForm({ ...form, markup_pct: markup, sell_price: autoSell });
              }} className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Sell Price ($) <span className="text-emerald-600 font-medium">{form.unit_cost > 0 && form.markup_pct > 0 ? 'auto-calculated' : 'manual'}</span></label>
              <input type="number" step="0.01" value={form.sell_price || ''} onChange={e => setForm({ ...form, sell_price: parseFloat(e.target.value) || 0 })}
                className={`w-full px-3 py-2 border rounded-lg text-sm outline-none ${form.unit_cost > 0 && form.markup_pct > 0 ? 'bg-emerald-50 border-emerald-300 text-emerald-900 font-bold' : 'bg-white/90 border-slate-300 text-slate-900'}`} />
            </div>
            <div>
              <label className="text-xs text-slate-500">Supplier</label>
              <select value={form.supplier_id || ''} onChange={e => setForm({ ...form, supplier_id: e.target.value })}
                className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none">
                <option value="">— None —</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-xs text-slate-500">Notes</label>
              <input value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })}
                className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => save(false)} disabled={saving} data-testid="catalog-item-save-btn"
              className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg font-semibold">
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Item'}
            </button>
            {!isEdit && (
              <button onClick={() => save(true)} disabled={saving} data-testid="catalog-item-save-another-btn"
                title="Add this item and keep the form open for the next one (keeps category & supplier)"
                className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-lg font-semibold">
                {saving ? 'Saving...' : 'Add + Next Item'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── ADD/EDIT SUPPLIER MODAL ──
  const SupplierModal = () => {
    const [form, setForm] = useState(editSupplier || { name: '', contact_name: '', email: '', phone: '', account_number: '', notes: '' });
    const isEdit = !!editSupplier;
    const save = async () => {
      try {
        if (isEdit) { await axios.put(`${API}/suppliers/${editSupplier.id}`, form); toast.success('Updated'); }
        else { await axios.post(`${API}/suppliers`, form); toast.success('Created'); }
        setShowAddSupplier(false); setEditSupplier(null); load();
      } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    };
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="bg-white rounded-xl border border-slate-200 w-full max-w-md p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-900">{isEdit ? 'Edit Supplier' : 'New Supplier'}</h3>
            <button onClick={() => { setShowAddSupplier(false); setEditSupplier(null); }}><X className="w-5 h-5 text-slate-500" /></button>
          </div>
          {['name', 'contact_name', 'email', 'phone', 'account_number', 'notes'].map(f => (
            <div key={f}>
              <label className="text-xs text-slate-500 capitalize">{f.replace('_', ' ')}{f === 'name' ? ' *' : ''}</label>
              <input value={form[f] || ''} onChange={e => setForm({ ...form, [f]: e.target.value })}
                type={f === 'email' ? 'email' : 'text'}
                className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" />
            </div>
          ))}
          <button onClick={save} className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold">
            {isEdit ? 'Save Changes' : 'Add Supplier'}
          </button>
        </div>
      </div>
    );
  };

  if (loading) return <div className="text-center py-12 text-slate-500">Loading catalog...</div>;

  return (
    <div className="space-y-4" data-testid="catalog-management">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Consumables Catalog</h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage pricing, markup, suppliers, and ordering</p>
      </div>

      <div className="flex gap-1 bg-white p-1 rounded-xl border border-slate-200 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              tab === t.id ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
            }`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'catalog' && (
        <CatalogTabView
          items={items} suppliers={suppliers} categories={categories}
          search={search} setSearch={setSearch}
          filterCat={filterCat} setFilterCat={setFilterCat}
          openCats={openCats} setOpenCats={setOpenCats}
          onAddItem={() => { setShowAddItem(true); setEditItem(null); }}
          onEditItem={(item) => { setEditItem(item); setShowAddItem(true); }}
          onDeleteItem={deleteItem}
        />
      )}
      {tab === 'suppliers' && <SuppliersTab />}
      {tab === 'order' && <OrderTab />}
      {tab === 'markup' && <MarkupTab />}

      {showAddItem && <ItemModal />}
      {showAddSupplier && <SupplierModal />}
    </div>
  );
}
